import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous sentinel.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next sentinel.
 * @property {HTMLButtonElement} [viewMoreButton] - Optional click-to-load View more button.
 * @property {HTMLElement} [viewMoreProgress] - Optional progress bar for View more.
 * @property {HTMLElement} [viewMoreProgressInner] - Optional progress bar fill element.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isRestoringPagination = false;

  /** @type {AbortController | null} */
  #pageshowAbortController = null;

  static SCROLL_STORAGE_KEY = 'dstrezzed-plp-scroll';

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // Back from PDP (or deep-link to ?page=N#card): rehydrate earlier pages, then restore scroll.
    // Observer starts after restore so the top sentinel does not race prepends.
    void this.#restorePaginationState().finally(() => {
      this.#observeViewMore();
    });

    // bfcache already has accumulated pages + scroll; drop the snapshot so a later
    // full load of the same URL does not apply a stale scrollY.
    this.#pageshowAbortController?.abort();
    this.#pageshowAbortController = new AbortController();
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.#clearScrollSnapshot();
      },
      { signal: this.#pageshowAbortController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#pageshowAbortController?.abort();
    this.#pageshowAbortController = null;
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;
          if (this.#isRestoringPagination) return;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Reads PLP scroll snapshot written before navigating to a product card.
   * @returns {{ key: string, scrollY: number, page: number, hash: string } | null}
   */
  #readScrollSnapshot() {
    try {
      const raw = sessionStorage.getItem(PaginatedList.SCROLL_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * Clears PLP scroll snapshot after a successful restore (or mismatch).
   */
  #clearScrollSnapshot() {
    try {
      sessionStorage.removeItem(PaginatedList.SCROLL_STORAGE_KEY);
    } catch {
      // sessionStorage may be unavailable
    }
  }

  /**
   * @returns {string} pathname + search (no hash) for matching stored scroll state
   */
  #currentPlpStorageKey() {
    const url = new URL(window.location.href);
    url.hash = '';
    return `${url.pathname}${url.search}`;
  }

  /**
   * When landing on ?page>1 (browser back from PDP), prepend all earlier pages so the
   * grid matches the infinite-scroll session, then restore scrollY / hash target.
   */
  async #restorePaginationState() {
    const url = new URL(window.location.href);
    const urlPage = Number(url.searchParams.get('page') || '1');
    const pendingHash = document.documentElement.dataset.plpRestoreHash || url.hash.replace(/^#/, '');
    const hash = pendingHash;
    const snapshot = this.#readScrollSnapshot();
    const snapshotMatches = Boolean(snapshot && snapshot.key === this.#currentPlpStorageKey());

    // Page 1: apply stored promo heights + scrollY (no page prepend). Hash alone is not enough
    // when collection-promo height settles after paint.
    if (urlPage <= 1) {
      if (snapshotMatches && snapshot && typeof snapshot.scrollY === 'number') {
        window.scrollTo({ top: snapshot.scrollY, behavior: 'instant' });
      } else if (hash) {
        this.#scrollToHash(hash);
      }

      if (hash) {
        const nextUrl = new URL(window.location.href);
        nextUrl.hash = hash;
        history.replaceState(history.state, '', nextUrl.toString());
      }

      if (snapshotMatches) this.#clearScrollSnapshot();
      this.#revealRestoredPagination();
      return;
    }

    // Eager previous-page rehydrate is for back-from-PDP (snapshot) or a product hash deep link.
    // Plain ?page=N landings keep Horizon's top-sentinel behavior.
    if (!snapshotMatches && !hash) {
      return;
    }

    this.#isRestoringPagination = true;
    const previousScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';

    try {
      // Keep prepending until the first card is page 1 (or no more pages).
      // Use replaceState-free silent prepends so we keep ?page=N#hash from the PDP trip.
      // Safety cap avoids infinite loops on bad pagination data.
      let guard = 0;
      while (guard < 50) {
        guard += 1;
        const previousPage = this.#getPage('previous');
        if (!previousPage || !this.#shouldUsePage(previousPage)) break;

        const prepended = await this.#prependPage(previousPage, { updateHistory: false });
        if (!prepended) break;
      }

      // Allow layout / aspect-ratio helpers a frame before measuring scroll.
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      if (snapshotMatches && snapshot && typeof snapshot.scrollY === 'number') {
        window.scrollTo({ top: snapshot.scrollY, behavior: 'instant' });
      } else if (hash) {
        this.#scrollToHash(hash);
      }

      // Put the product hash back without triggering a native anchor jump.
      if (hash) {
        const nextUrl = new URL(window.location.href);
        nextUrl.hash = hash;
        history.replaceState(history.state, '', nextUrl.toString());
      }
    } finally {
      if (snapshotMatches) this.#clearScrollSnapshot();
      history.scrollRestoration = previousScrollRestoration;
      this.#isRestoringPagination = false;
      this.#syncViewMoreButton();
      // Reveal only after scroll is applied so the first paint is the restored viewport.
      this.#revealRestoredPagination();
    }
  }

  /**
   * Removes restore chrome: skeletons, pending hash, and the restoring class.
   */
  #revealRestoredPagination() {
    const { grid } = this.refs;
    if (grid) {
      grid.querySelectorAll('[data-plp-skeleton]').forEach((node) => node.remove());
    } else {
      document.querySelectorAll('[data-plp-skeleton]').forEach((node) => node.remove());
    }

    delete document.documentElement.dataset.plpRestoreHash;
    document.documentElement.classList.remove('plp-pagination-restoring');
    document.documentElement.style.removeProperty('--plp-restore-min-height');
  }

  /**
   * @param {string} hash
   */
  #scrollToHash(hash) {
    if (!hash) return;
    const card = document.getElementById(hash);
    if (card) {
      card.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }

  /**
   * Ensures a page HTML is cached.
   * @param {number} pageNumber
   * @param {URL} [url]
   */
  async #ensurePageCached(pageNumber, url) {
    if (this.pages.has(pageNumber)) return;
    await this.#fetchSpecificPage(pageNumber, url);
  }

  /**
   * Ensures a page is in the cache, then prepends its cards.
   * @param {{ page: number, url: URL }} pageInfo
   * @param {{ updateHistory?: boolean }} [options]
   * @returns {Promise<boolean>} Whether cards were prepended
   */
  async #prependPage(pageInfo, { updateHistory = true } = {}) {
    const { grid } = this.refs;
    if (!grid) return false;

    await this.#ensurePageCached(pageInfo.page, pageInfo.url);
    const pageItemElements = this.#getGridForPage(pageInfo.page);
    if (!pageItemElements) return false;

    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    grid.prepend(...pageItemElements);
    this.#aspectRatioHelper?.processNewElements();

    if (updateHistory) {
      history.pushState('', '', pageInfo.url.toString());

      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    }

    return true;
  }

  async #renderPreviousPage() {
    if (this.#isRestoringPagination) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    await this.#prependPage(previousPage, { updateHistory: true });
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Public click handler for the Dstrezzed "View more" button.
   * @param {Event} [event]
   */
  async renderNextPage(event) {
    event?.preventDefault?.();

    const { viewMoreButton } = this.refs;
    if (viewMoreButton instanceof HTMLButtonElement) {
      if (viewMoreButton.disabled || viewMoreButton.getAttribute('aria-busy') === 'true') return;
      viewMoreButton.setAttribute('aria-busy', 'true');
      viewMoreButton.disabled = true;
    }

    try {
      await this.#renderNextPage();
    } finally {
      this.#syncViewMoreButton();
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#syncViewMoreButton();
      return;
    }
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) {
        this.#syncViewMoreButton();
        return;
      }
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    this.#syncViewMoreButton();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * Syncs View more / end-of-collection UI after a page load.
   * Button mode: enable/disable the button. Auto-load: reveal end text when done.
   */
  #syncViewMoreButton() {
    this.#syncViewMoreProgress();

    const { viewMoreButton } = this.refs;
    const wrapper =
      (viewMoreButton instanceof HTMLElement && viewMoreButton.closest('.product-grid__view-more')) ||
      this.querySelector('.product-grid__view-more');
    const nextPage = this.#getPage('next');
    const hasNextPage = this.#shouldUsePage(nextPage);

    if (!hasNextPage) {
      if (wrapper instanceof HTMLElement) {
        wrapper.classList.add('product-grid__view-more--ended');
      }

      if (viewMoreButton instanceof HTMLButtonElement) {
        viewMoreButton.removeAttribute('aria-busy');
        viewMoreButton.disabled = true;
      }

      return;
    }

    if (wrapper instanceof HTMLElement) {
      wrapper.classList.remove('product-grid__view-more--ended');
    }

    if (viewMoreButton instanceof HTMLButtonElement) {
      viewMoreButton.removeAttribute('aria-busy');
      viewMoreButton.disabled = false;
    }
  }

  /**
   * Updates the View more progress bar from currently rendered cards / total items.
   */
  #syncViewMoreProgress() {
    const { viewMoreProgress, viewMoreProgressInner, cards } = this.refs;
    if (!(viewMoreProgress instanceof HTMLElement) || !(viewMoreProgressInner instanceof HTMLElement)) return;

    const totalItems = Number(viewMoreProgress.dataset.totalItems);
    if (!Number.isFinite(totalItems) || totalItems <= 0) return;

    const shownItems = Array.isArray(cards)
      ? cards.filter((card) => !(card instanceof HTMLElement && card.hasAttribute('data-promo-block'))).length
      : cards && !(cards instanceof HTMLElement && cards.hasAttribute('data-promo-block'))
        ? 1
        : 0;
    const percent = Math.min(100, Math.round((shownItems / totalItems) * 100));

    viewMoreProgressInner.style.width = `${percent}%`;
    viewMoreProgress.setAttribute('aria-valuenow', String(percent));
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
